import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { saveCharacter, saveCharacterOnPageExit } from "../clients/characters.client";
import { toCharacterUpdateRequest } from "../models/Character";

const AUTO_SAVE_DELAY = 1000;
const serializeCharacter = (character) => JSON.stringify(toCharacterUpdateRequest(character));

export function useCharacterAutoSave(character, token) {
  const { t } = useTranslation();
  const characterRef = useRef(character);
  const characterIdRef = useRef(null);
  const lastSavedRef = useRef(null);
  const timerRef = useRef(null);
  const exitSaveStartedRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [isSaveError, setIsSaveError] = useState(false);

  characterRef.current = character;

  const saveNow = useCallback(async (message = t("save.saved"), characterOverride = null) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const currentCharacter = characterOverride || characterRef.current;
    if (!currentCharacter || !token) {
      return;
    }

    const serialized = serializeCharacter(currentCharacter);
    if (serialized === lastSavedRef.current) {
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);
    setIsSaveError(false);
    const response = await saveCharacter(currentCharacter, token);

    if (response.isError) {
      setSaveMessage(t("save.error"));
      setIsSaveError(true);
    } else {
      lastSavedRef.current = serialized;
      setSaveMessage(message);
    }
    setIsSaving(false);
  }, [t, token]);

  useEffect(() => {
    if (!character) {
      return;
    }

    const serialized = serializeCharacter(character);
    if (characterIdRef.current !== character.id) {
      characterIdRef.current = character.id;
      lastSavedRef.current = serialized;
      exitSaveStartedRef.current = false;
      return;
    }

    if (serialized === lastSavedRef.current) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      saveNow(t("save.autoSaved"));
    }, AUTO_SAVE_DELAY);
  }, [character, saveNow, t]);

  useEffect(() => {
    const savePendingChanges = () => {
      const currentCharacter = characterRef.current;
      if (!currentCharacter || !token) {
        return;
      }

      if (!exitSaveStartedRef.current && serializeCharacter(currentCharacter) !== lastSavedRef.current) {
        exitSaveStartedRef.current = true;
        saveCharacterOnPageExit(currentCharacter, token);
      }
    };

    window.addEventListener("pagehide", savePendingChanges);
    window.addEventListener("beforeunload", savePendingChanges);

    return () => {
      savePendingChanges();
      window.removeEventListener("pagehide", savePendingChanges);
      window.removeEventListener("beforeunload", savePendingChanges);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [token]);

  const dismissSaveMessage = useCallback(() => {
    setSaveMessage(null);
    setIsSaveError(false);
  }, []);

  const retrySave = useCallback(() => saveNow(t("save.autoSaved")), [saveNow, t]);
  return {
    dismissSaveMessage,
    isSaveError,
    isSaving,
    retrySave,
    saveMessage,
    saveNow,
  };
}
